import axios from 'axios';
import { MANAGEMENT_TOKEN_KEY, MANAGEMENT_ACCOUNT_KEY } from '../constants/authKeys';
import { getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import {
  JobApplication,
  JobShift, 
  JobShiftCreate, 
  JobShiftUpdate,
  JobPostingTemplate,
  JobPostingTemplateCreate,
  LineMessageSend,
  LineMessageLog,
  LineMessageResponse,
  StatsData,
  SegmentCountResponse,
  JobPosition,
  JobApplicationWithShift,
  LaborConditionsAgreementInfo,
  // お知らせ関連の型をindex.tsからインポート
  AnnouncementCategory,
  Announcement,
  AnnouncementDetail,
  UnreadCountResponse,
  StoreInfo,
  ImageUploadResponse,
  StoreUpdateForm,
  SystemSettings,
  Industry,
  JobPositionWithIndustry,
  StoreNotificationId, 
  StoreNotificationIdCreate, 
  StoreNotificationIdUpdate, 
  StoreNotificationIdList,
  // 受動喫煙防止措置関連の型を追加
  SmokingPreventionMeasure,
  SmokingPreventionMeasureList,
  AdminStoreCreateForm,
  AdminStoreUpdateForm,
  AdminStore,
  AdminStoreList,
  AdminStoreDetail,
  AdminCompanyOption,
  AdminStoreInitialSetupStatus,
  IdentityUser,
  IdentityUserDetail,
  SystemSettingsUpdate,
  AdminAnnouncementResponse,
  AdminAnnouncementListResponse,
  AdminAnnouncementCreateRequest,
  AdminAnnouncementUpdateRequest,
  FAQ,
  FAQUpdate,
  BillingRecordsResponse,
  ManagementAccountLoginRequest,
  ManagementAccountLoginResponse,
  ManagementAccount,
  ManagementAccountUpdate,
  CompanyDetail,
  CompanyUpdate,
  BillingRecord,
  BillingRecordWithDetails,
  EmailSetting,
  EmailSettingUpdate,
  StoreBrief,
  StoreApplication,
  StoreApplicationCreate,
  StoreApplicationStatus,
  FeeComparisonResponse,
  ManagementRequestCreate,
  ManagementRequestResponse,
  StoreClosingCreate,
  StoreClosingResponse,
  // ゲーミフィケーション: スキルランク関連の型
  SkillTier,
  SkillDefinition,
  SkillDefinitionCreate,
  SkillDefinitionUpdate,
  SkillImportResponse,
  ApproveSkillsResponse,
  CrossStoreSkillsResponse,
  CanonicalSkillAdminItem,
  CanonicalMappingAudit,
  // ゲーミフィケーション: 店舗ランク設定（旧051。スキル総数ベースに一本化）
  StoreRankConfig,
  StoreRankConfigUpdate,
  RankRecalculateResult,
  // ゲーミフィケーション: クルー詳細バンドル / ランクサマリ / 育成アドバイス
  CrewGamificationBundle,
  CrewRanksSummary,
  AdviceObject,
  AdviceStatus,
  StoreManualAdvice,
  UnacquiredSkillsForCrew,
  // ゲーミフィケーション: 特別オファー / 招待状
  SpecialOffer,
  SpecialOfferCreate,
  SpecialOfferType,
  SpecialOfferTemplate,
  SpecialOfferTemplateInput,
  // ゲーミフィケーション: 今すぐ募集 ランク別配信
  BroadcastByRankTarget,
  BroadcastByRankCountResponse,
  BroadcastByRankRequest,
  BroadcastByRankResponse,
} from '../types/index';

type ApiErrorDetail = {
  error_code?: string;
  message?: string;
  details?: Record<string, unknown>;
  provider_error?: string | null;
};

// 環境変数からAPI URLを取得（Docker/本番環境対応）
// REACT_APP_API_URL: docker-compose.dev.ymlで"/api"に設定
// ローカル開発時はsetupProxy.jsでプロキシ経由でバックエンドに接続
const API_URL = process.env.REACT_APP_API_URL || '/api';
// Axiosインスタンスの作成
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// リクエスト処理
apiClient.interceptors.request.use(
  (config) => {
    // 必要に応じて認証トークンをここで追加
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 課題117: 管理JWT失効(401)時にログイン画面へリダイレクトする。
// 管理JWT系画面（クルー管理・クルー詳細・就業予定表・店舗選択 等）はトークン失効時に
// 「トークンの有効期限が切れています」を表示するだけで、ユーザーが手動で再ログインするしか
// なかった。401 の失効主体は実質 management_token のみ（403=権限・404=不在は別扱い）なので、
// レスポンスインターセプタで一元的に検知し、トークンを破棄してログイン画面へ遷移する。
// 同時多発401（クルー管理は並列fetch）でも遷移は一度きりにする。
let redirectingToLogin = false;

// レスポンス処理
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      axios.isAxiosError(error) &&
      error.response?.status === 401 &&
      typeof window !== 'undefined' &&
      // 失効しうる管理トークンを保持していた場合のみ発火（店舗ログイン等は巻き込まない）
      localStorage.getItem(MANAGEMENT_TOKEN_KEY) &&
      window.location.pathname !== '/login' &&
      !redirectingToLogin
    ) {
      redirectingToLogin = true;
      // logoutManagement と同じ2キーを破棄（ManagementAuthContext は lazy initializer で
      // localStorage を読むため、ハードリロードで isManagementUser=false のログイン画面になる）
      localStorage.removeItem(MANAGEMENT_TOKEN_KEY);
      localStorage.removeItem(MANAGEMENT_ACCOUNT_KEY);
      // 失効画面へ戻らせないよう replace で遷移（既存のルートガードも Navigate replace を使用）
      window.location.replace('/login');
    }
    return Promise.reject(error);
  }
);

// サーバから返る文字列に内部情報（SQL・スタックトレース・DBドライバ名等）が
// 含まれていないか判定する。バックエンド側（app/core/error_handling.py）でも
// 同じ判定をしているが、古いバックエンドや将来の別APIから漏れた場合の保険として
// クライアント側にも同じガードを置く（設計書 §4-5 の層3）。
const SENSITIVE_SERVER_TEXT_PATTERNS = [
  '[sql:',
  '[parameters:',
  'select ',
  'insert into',
  'delete from',
  ' from ',
  ' where ',
  'sqlalchemy',
  'pg8000',
  'psycopg',
  'does not exist',
  'relation "',
  'column "',
  'constraint "',
  'duplicate key value',
  'traceback (most recent call last)',
  'file "',
  '.py", line',
  'site-packages',
  'alembic',
  'postgresql://',
  'integrityerror',
  'programmingerror',
  'operationalerror',
];

export const containsSensitiveServerText = (text: unknown): boolean => {
  if (typeof text !== 'string' || !text) return false;
  const lowered = text.toLowerCase();
  return SENSITIVE_SERVER_TEXT_PATTERNS.some((pattern) => lowered.includes(pattern));
};

export const getApiErrorMessage = (error: unknown, fallbackMessage: string): string => {
  if (axios.isAxiosError(error)) {
    // 5xx はユーザーが対処できないため文言に情報価値が無く、内部情報の漏洩経路に
    // なるだけ。サーバ文字列を一切採用せずフォールバックにする（設計書 §4-5）。
    const status = error.response?.status;
    if (typeof status === 'number' && status >= 500) {
      return fallbackMessage;
    }

    const detail = error.response?.data?.detail;

    if (typeof detail === 'string' && detail.trim()) {
      // 4xx でも内部情報を含む文字列はフォールバックに差し替える
      return containsSensitiveServerText(detail) ? fallbackMessage : detail;
    }

    // FastAPIのリクエスト検証エラーは detail が [{loc, msg, type}, ...] の配列になる。
    // loc にパラメータ名などの内部構造が含まれるため、そのまま画面に出さずフォールバックにする。
    if (Array.isArray(detail)) {
      return fallbackMessage;
    }

    if (detail && typeof detail === 'object') {
      const typedDetail = detail as ApiErrorDetail;
      if (typedDetail.message && typedDetail.provider_error) {
        const combined = `${typedDetail.message} (${typedDetail.provider_error})`;
        return containsSensitiveServerText(combined) ? fallbackMessage : combined;
      }
      if (typedDetail.message) {
        return containsSensitiveServerText(typedDetail.message)
          ? fallbackMessage
          : typedDetail.message;
      }
      return fallbackMessage;
    }

    // サーバーから応答はあるが detail を解釈できない場合、"Request failed with
    // status code 422" のような生のaxiosメッセージをそのまま出さずフォールバックにする。
    if (error.response) {
      return fallbackMessage;
    }

    // レスポンス自体が無い（ネットワーク断・タイムアウト等）場合のみaxiosのメッセージを使う。
    if (typeof error.message === 'string' && error.message.trim()) {
      return error.message;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallbackMessage;
};

/** FastAPI のリクエスト検証エラー1件（detail 配列の要素） */
type FastApiValidationError = {
  loc?: unknown[];
  msg?: string;
  type?: string;
  ctx?: Record<string, unknown>;
};

/**
 * pydantic のエラー種別から日本語の入力エラー文を組み立てる。
 *
 * サーバの `msg`（英語＋内部フィールド名）はそのまま画面に出さない方針
 * （getApiErrorMessage が detail 配列をフォールバックに落としているのと同じ理由）なので、
 * 種別と画面上のラベルからこちらで文言を作る。
 */
const buildValidationMessage = (item: FastApiValidationError, label: string): string => {
  const ctx = item?.ctx as { max_length?: number } | undefined;
  switch (item?.type) {
    case 'missing':
    case 'string_type':
    case 'string_too_short':
      return `${label}を入力してください`;
    case 'string_too_long':
      return typeof ctx?.max_length === 'number'
        ? `${label}は${ctx.max_length}文字以内で入力してください`
        : `${label}が長すぎます`;
    case 'value_error':
    case 'string_pattern_mismatch':
      return `${label}の形式が正しくありません`;
    default:
      return `${label}の入力内容をご確認ください`;
  }
};

/**
 * 422（リクエスト検証エラー）を「入力欄ごとのエラー文」に変換する。
 *
 * 共通ハンドラは detail 配列を「リクエストの形式が不正です」という一文に丸めるため、
 * どの項目が悪いのかが画面から分からなかった（設計書 §3.4(b)）。
 *
 * `fieldLabels` に載っている項目だけを返す。画面に入力欄が無い項目を helperText に
 * 出す先が無いためで、戻り値が空のときは呼び出し側で従来どおり全体エラーを表示する。
 */
export const extractValidationFieldErrors = (
  error: unknown,
  fieldLabels: Record<string, string>
): Record<string, string> => {
  if (!axios.isAxiosError(error) || error.response?.status !== 422) {
    return {};
  }

  const detail = error.response?.data?.detail;
  if (!Array.isArray(detail)) {
    return {};
  }

  const fieldErrors: Record<string, string> = {};
  for (const item of detail as FastApiValidationError[]) {
    // loc は ["body", "<フィールド名>"] 形式。query/path のエラーは入力欄に紐付かない
    const loc = Array.isArray(item?.loc) ? item.loc : [];
    if (loc.length < 2 || loc[0] !== 'body' || typeof loc[1] !== 'string') {
      continue;
    }
    const field = loc[1];
    const label = fieldLabels[field];
    // 同一フィールドに複数エラーが来た場合は最初の1件だけ出す
    if (!label || fieldErrors[field]) {
      continue;
    }
    fieldErrors[field] = buildValidationMessage(item, label);
  }
  return fieldErrors;
};

/**
 * APIエラーから特定フィールドに紐付くエラー情報を抽出する。
 * 主にLINE認証情報の重複（バックエンドが409 + detail文字列で返す）を
 * フィールドhelperText表示用に変換するために利用する。
 *
 * 戻り値: 該当するフィールドエラーが見つかれば { field, message }、無ければ null
 */
export const extractFieldErrorFromApiError = (
  error: unknown
): { field: string; message: string } | null => {
  if (!axios.isAxiosError(error)) {
    return null;
  }

  const detail = error.response?.data?.detail;
  const detailText = typeof detail === 'string'
    ? detail
    : (detail && typeof detail === 'object' && typeof (detail as ApiErrorDetail).message === 'string'
      ? (detail as ApiErrorDetail).message as string
      : '');

  if (!detailText) {
    return null;
  }

  // LINEチャネルアクセストークンの重複
  if (detailText.includes('アクセストークン')) {
    return { field: 'line_channel_access_token', message: detailText };
  }
  // LINEチャネルシークレットの重複
  if (detailText.includes('チャネルシークレット') || detailText.includes('シークレット')) {
    return { field: 'line_channel_secret', message: detailText };
  }

  return null;
};

// システム設定関連のAPI
export const systemSettingsApi = {
  // 管理画面用システム設定を取得
  getManagementSettings: async (): Promise<SystemSettings[]> => {
    const response = await apiClient.get<SystemSettings[]>('/system-settings/management/settings');
    return response.data;
  },

  // 全てのシステム設定を取得
  getAllSettings: async (): Promise<SystemSettings[]> => {
    const response = await apiClient.get<SystemSettings[]>('/system-settings/management/settings/all');
    return response.data;
  },

  // 特定のシステム設定を取得
  getById: async (settingId: string): Promise<SystemSettings> => {
    const response = await apiClient.get<SystemSettings>(`/system-settings/management/settings/${settingId}`);
    return response.data;
  },

  // システム設定を更新
  updateSetting: async (settingId: string, data: SystemSettingsUpdate): Promise<SystemSettings> => {
    const response = await apiClient.put<SystemSettings>(`/system-settings/management/settings/${settingId}`, data);
    return response.data;
  }
};

// 受動喫煙防止措置関連のAPI
export const smokingPreventionApi = {
  // 受動喫煙防止措置一覧取得
  getAll: async (params: {
    skip?: number;
    limit?: number;
    is_active?: boolean;
  } = {}): Promise<SmokingPreventionMeasureList> => {
    try {
      const response = await apiClient.get<SmokingPreventionMeasureList>('/smoking-prevention-measures/', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // アクティブな受動喫煙防止措置のみ取得
  getActive: async (): Promise<SmokingPreventionMeasure[]> => {
    try {
      const response = await apiClient.get<SmokingPreventionMeasureList>('/smoking-prevention-measures/', { 
        params: { is_active: true } 
      });
      return response.data.measures;
    } catch (error) {
      throw error;
    }
  },

  // 特定の受動喫煙防止措置取得
  getById: async (id: string): Promise<SmokingPreventionMeasure> => {
    try {
      const response = await apiClient.get<SmokingPreventionMeasure>(`/smoking-prevention-measures/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // 受動喫煙防止措置作成
  create: async (data: {
    code: string;
    name: string;
    description?: string;
    display_order?: number;
    is_active?: boolean;
  }): Promise<SmokingPreventionMeasure> => {
    try {
      const response = await apiClient.post<SmokingPreventionMeasure>('/smoking-prevention-measures/', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // 受動喫煙防止措置更新
  update: async (id: string, data: {
    code?: string;
    name?: string;
    description?: string;
    display_order?: number;
    is_active?: boolean;
  }): Promise<SmokingPreventionMeasure> => {
    try {
      const response = await apiClient.put<SmokingPreventionMeasure>(`/smoking-prevention-measures/${id}`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // 受動喫煙防止措置削除
  delete: async (id: string): Promise<void> => {
    try {
      await apiClient.delete(`/smoking-prevention-measures/${id}`);
    } catch (error) {
      throw error;
    }
  }
};

// 業種管理API
export const industryApi = {
  // 業種一覧取得
  getAll: async (params = {}): Promise<Industry[]> => {
    const response = await apiClient.get<Industry[]>('/industries/', { params });
    return response.data;
  },

  // アクティブな業種のみ取得
  getActive: async (): Promise<Industry[]> => {
    const response = await apiClient.get<Industry[]>('/industries/', { 
      params: { is_active: true } 
    });
    return response.data;
  },

  // 特定の業種取得
  getById: async (id: string): Promise<Industry> => {
    const response = await apiClient.get<Industry>(`/industries/${id}`);
    return response.data;
  },

  // 業種作成
  create: async (data: {
    name: string;
    description?: string;
    display_order?: number;
    is_active?: boolean;
  }): Promise<Industry> => {
    const response = await apiClient.post<Industry>('/industries/', data);
    return response.data;
  },

  // 業種更新
  update: async (id: string, data: {
    name?: string;
    description?: string;
    display_order?: number;
    is_active?: boolean;
  }): Promise<Industry> => {
    const response = await apiClient.put<Industry>(`/industries/${id}`, data);
    return response.data;
  },

  // 業種削除
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/industries/${id}`);
  }
};

// 職種管理API
export const jobPositionApi = {
  // 職種一覧取得
  getAll: async (storeId: string | null, params = {}): Promise<JobPosition[]> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }

    const queryParams = { ...params, store_id: storeId };
    const response = await apiClient.get<JobPosition[]>('/job-positions/', { params: queryParams });
    return response.data;
  },

  // 業種情報を含む職種一覧取得
  getAllWithIndustry: async (storeId: string | null, params = {}): Promise<JobPositionWithIndustry[]> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }

    const queryParams = { ...params, store_id: storeId };
    const response = await apiClient.get<JobPositionWithIndustry[]>('/job-positions/with-industry', { params: queryParams });
    return response.data;
  },

  // アクティブな職種のみ取得（業種情報含む）
  getActiveWithIndustry: async (storeId: string | null): Promise<JobPositionWithIndustry[]> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }

    const response = await apiClient.get<JobPositionWithIndustry[]>('/job-positions/with-industry', { 
      params: { is_active: true, store_id: storeId } 
    });
    return response.data;
  },

  // 特定の職種取得
  getById: async (id: string, storeId: string): Promise<JobPosition> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }

    const response = await apiClient.get<JobPosition>(`/job-positions/${id}`, {
      params: { store_id: storeId }
    });
    return response.data;
  },

  // 特定の職種取得（業種情報含む）
  getByIdWithIndustry: async (id: string, storeId: string): Promise<JobPositionWithIndustry> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    
    const response = await apiClient.get<JobPositionWithIndustry>(`/job-positions/${id}/with-industry`, {
      params: { store_id: storeId }
    });
    return response.data;
  },

  // 職種作成
  create: async (data: {
    store_id: string;
    name: string;
    is_active: boolean;
    industry_id?: string;
    display_order?: number;
  }): Promise<JobPosition> => {
    const createData = {
      store_id: data.store_id,
      name: data.name,
      is_active: data.is_active,
      industry_id: data.industry_id || null,
      display_order: data.display_order ?? 0,
    };

    const response = await apiClient.post<JobPosition>('/job-positions/', createData);
    return response.data;
  },

  // 職種更新
  update: async (id: string, data: {
    name?: string;
    is_active?: boolean;
    industry_id?: string;
    display_order?: number;
  }, storeId: string): Promise<JobPosition> => {
    const response = await apiClient.put<JobPosition>(`/job-positions/${id}`, data, {
      params: { store_id: storeId }
    });
    return response.data;
  },

  // 職種削除
  delete: async (id: string, storeId: string): Promise<void> => {
    await apiClient.delete(`/job-positions/${id}`, {
      params: { store_id: storeId }
    });
  },

  // アクティブな職種のみ取得
  getActive: async (storeId: string | null): Promise<JobPosition[]> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }

    const response = await apiClient.get<JobPosition[]>('/job-positions/', { 
      params: { is_active: true, store_id: storeId } 
    });
    return response.data;
  }
};

// 今すぐ募集テンプレート関連のAPI
export const jobPostingApi = {
  // 全テンプレート取得 - store_idを必須パラメータとして追加
  getAll: async (params = {}, storeId: string | null): Promise<JobPostingTemplate[]> => {
    const queryParams = { ...params, store_id: storeId };
    const response = await apiClient.get<JobPostingTemplate[]>('/templates/job-posting/', { params: queryParams });
    return response.data;
  },

  // 特定のテンプレート取得 - store_idを必須パラメータとして追加
  getById: async (id: string, storeId: string): Promise<JobPostingTemplate> => {
    const response = await apiClient.get<JobPostingTemplate>(`/templates/job-posting/${id}`, {
      params: { store_id: storeId }
    });
    return response.data;
  },

  // テンプレート作成 - store_idを必須パラメータとして追加
  create: async (template: JobPostingTemplateCreate, storeId: string | null): Promise<JobPostingTemplate> => {
    const response = await apiClient.post<JobPostingTemplate>('/templates/job-posting/', template, {
      params: { store_id: storeId }
    });
    return response.data;
  },

  // テンプレート更新 - store_idを必須パラメータとして追加
  update: async (id: string, template: JobPostingTemplateCreate, storeId: string | null): Promise<JobPostingTemplate> => {
    const response = await apiClient.put<JobPostingTemplate>(`/templates/job-posting/${id}`, template, {
      params: { store_id: storeId }
    });
    return response.data;
  },

  // テンプレート削除 - store_idを必須パラメータとして追加
  delete: async (id: string, storeId: string | null): Promise<void> => {
    await apiClient.delete(`/templates/job-posting/${id}`, {
      params: { store_id: storeId }
    });
  }
};

// 労働条件通知書関連のAPI
export const laborConditionsApi = {
  // 応募IDから同意済み労働条件通知書の情報を取得（存在しない場合は404）
  getAgreementByApplication: async (
    applicationId: string,
    storeId: string
  ): Promise<LaborConditionsAgreementInfo> => {
    const response = await apiClient.get<LaborConditionsAgreementInfo>(
      `/templates/labor-conditions/agreements/by-application/${applicationId}`,
      { params: { store_id: storeId } }
    );
    return response.data;
  },

  // 同意済み労働条件通知書PDFのダウンロードURLを生成（aタグ/window.openで開く用）
  getDownloadUrl: (agreementId: string, storeId: string): string => {
    const base = /^https?:\/\//.test(API_URL) ? API_URL : `${window.location.origin}${API_URL}`;
    return `${base}/templates/labor-conditions/agreements/${agreementId}/download?store_id=${encodeURIComponent(storeId)}`;
  },
};

// LINE配信関連のAPI
export const lineApi = {
  // メッセージ送信
  sendMessage: async (messageData: LineMessageSend): Promise<LineMessageResponse> => {
    try {
      const { template_id, message, target_segment, store_id, manual_text, shift_id, ...optionFields } = messageData;

      // リクエストボディ（LineMessageSendスキーマに従ってstore_idを含める）
      // manual_text は手入力分。バックエンドが定型文の末尾に連結する。
      // shift_id は配信時に作成したシフトのID。バックエンドの「詳細を見る」URL生成で使う。
      // 含めないと最古のアクティブシフトにフォールバックし、メッセージ本文とURLがズレる。
      const requestBody = {
        template_id: template_id,
        message: message.trim(),
        target_segment: target_segment,
        store_id: store_id,  // バックエンドのスキーマで必須
        ...(manual_text && manual_text.trim() ? { manual_text: manual_text.trim() } : {}),
        ...(shift_id ? { shift_id } : {})
      };

      // クエリパラメータ（broadcast.pyのAPIパラメータに対応）
      const params: Record<string, any> = {
        store_id: store_id,  // クエリパラメータとしても必要
        button_label: optionFields.button_label || "詳細を見る",
        use_button: optionFields.use_button !== undefined ? optionFields.use_button : true,
        use_shifts: optionFields.use_shifts !== undefined ? optionFields.use_shifts : true
      };
      // APIリクエスト送信
      const response = await apiClient.post<LineMessageResponse>(
        "/line/send-message/",
        requestBody, // store_idを含むボディ
        { 
          params: params,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      return response.data;
    } catch (error: any) {
      throw error;
    }
  },

  getMessageLogs: async (
    storeId: string | null,
    templateId?: string
  ): Promise<LineMessageLog[]> => {
    try {
      // テンプレートIDが指定されている場合はクエリパラメータに追加
      const params = templateId
        ? { store_id: storeId, template_id: templateId }
        : { store_id: storeId };

      const response = await apiClient.get<LineMessageLog[]>(
        "/line/message-logs/",
        { params }
      );

      // 日付文字列が適切にパースされるように処理
      const logs: LineMessageLog[] = response.data.map((log: any) => ({
        ...log,
        sent_at: new Date(log.sent_at).toISOString(), // 確実にISOフォーマットで扱う
      }));

      return logs;
    } catch (error) {
      throw error;
    }
  },

  // LINEユーザー統計取得
  getStats: async (storeId: string | null, params?: { month?: 'current' | 'last' }): Promise<StatsData> => {
    try {
      const queryParams: any = { store_id: storeId };
      
      // 月指定パラメータがある場合は追加
      if (params?.month) {
        queryParams.month = params.month;
      }
      
      const response = await apiClient.get<StatsData>("/line/stats/", {
        params: queryParams,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // セグメント別ユーザー数取得
  getSegmentCount: async (
    storeId: string | null,
    segment: string
  ): Promise<SegmentCountResponse> => {
    try {
      const response = await apiClient.get<SegmentCountResponse>(
        `/line/segment-count/${segment}`,
        {
          params: { store_id: storeId },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // LINEメッセージを持つテンプレート一覧取得
  getTemplatesWithLineMessage: async (storeId: string | null): Promise<any[]> => {
    try {
      const response = await apiClient.get<any[]>(
        "/line/templates/",
        { params: { store_id: storeId } }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// 求人シフト関連のAPI追加
export const jobShiftApi = {
  // 全シフト取得
  getAll: async (params = {}): Promise<{ shifts: JobShift[], total: number }> => {
    const response = await apiClient.get<{ shifts: JobShift[], total: number }>('/job-shifts/', { params });
    return response.data;
  },

  // 特定のシフト取得
  getById: async (id: string, storeId: string): Promise<JobShift> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    const response = await apiClient.get<JobShift>(`/job-shifts/${id}`, {
      params: { store_id: storeId }
    });
    return response.data;
  },

  // 特定の店舗のシフト取得
  getByStoreId: async (storeId: string, params = {}): Promise<{ shifts: JobShift[], total: number }> => {
    const response = await apiClient.get<{ shifts: JobShift[], total: number }>(`/job-shifts/store/${storeId}`, { params });
    return response.data;
  },

  // シフト作成
  create: async (shift: JobShiftCreate, storeId: string | null): Promise<JobShift> => {
    const response = await apiClient.post<JobShift>('/job-shifts/', shift, {
      params: { store_id: storeId }
    });
    return response.data;
  },

  // シフト更新
  update: async (id: string, shift: JobShiftUpdate, storeId: string | null): Promise<JobShift> => {
    const response = await apiClient.put<JobShift>(`/job-shifts/${id}`, shift, {
      params: { store_id: storeId }  // クエリパラメータとして送る
    });
    return response.data;
  },

  // シフト削除
  delete: async (id: string, storeId: string | null): Promise<void> => {
    await apiClient.delete(`/job-shifts/${id}`, {
      params: { store_id: storeId }
    });
  },

  // テンプレートIDに基づくシフト取得
  getByTemplateId: async (templateId: number, params = {}): Promise<{ shifts: JobShift[], total: number }> => {
    const queryParams = { ...params, template_id: templateId };
    const response = await apiClient.get<{ shifts: JobShift[], total: number }>('/job-shifts/', { params: queryParams });
    return response.data;
  },

  // 応募者情報付きでシフト取得
  getWithApplicants: async (
    storeId: string,
    params: { from_date?: string; to_date?: string; search?: string; limit?: number } = {}
  ): Promise<{ shifts: any[], total: number }> => {
    const response = await apiClient.get<{ shifts: any[], total: number }>(`/job-shifts/store/${storeId}/with-applicants`, { params });
    return response.data;
  }
};

// 応募管理API - store_idを必須パラメータとして追加
export const applicationApi = {
  // 全応募を取得
  getAll: async (params = {}, storeId: string | null): Promise<JobApplication[]> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }

    const queryParams = { ...params, store_id: storeId };
    const response = await apiClient.get<JobApplication[]>('/recruiter/', { params: queryParams });
    return response.data;
  },
  // 全応募を取得
  getAllWithShift: async (params = {}, storeId: string | null): Promise<JobApplicationWithShift[]> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }

    const queryParams = { ...params, store_id: storeId };
    const response = await apiClient.get<JobApplicationWithShift[]>('/recruiter/with-shift/', { params: queryParams });
    return response.data;
  },

  // 特定の応募を取得
  getById: async (id: string, storeId: string): Promise<JobApplication> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }

    const response = await apiClient.get<JobApplication>(`/recruiter/${id}`, { 
      params: { store_id: storeId } 
    });
    return response.data;
  },

  // 求人IDに基づく応募を取得
  getByJobId: async (jobId: string, storeId: string): Promise<JobApplication[]> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }

    const response = await apiClient.get<JobApplication[]>('/recruiter/', { 
      params: { job_id: jobId, store_id: storeId } 
    });
    return response.data;
  },

  // 応募情報を更新（status: 採用/不採用等の遷移。accepted への遷移でランク即時導出・勤怠自動作成が走る）
  update: async (id: string, updateData: { status?: string; memo?: string; user_rank?: string | null }, storeId: string): Promise<JobApplication> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }

    const response = await apiClient.patch<JobApplication>(`/recruiter/${id}`, updateData, {
      params: { store_id: storeId }
    });
    return response.data;
  },

  // 応募を削除
  delete: async (id: string, storeId: string): Promise<void> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }

    await apiClient.delete(`/recruiter/${id}`, {
      params: { store_id: storeId }
    });
  },

  // 課題114: 採用確定済み応募の勤怠キャンセル（planned のみ。応募も cancelled に戻す）
  cancelAttendance: async (
    id: string,
    storeId: string,
    reason?: string
  ): Promise<{ success: boolean; application_id: string; work_attendance_id: string; status: string }> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }

    const response = await apiClient.post(
      `/recruiter/${id}/cancel-attendance`,
      { reason: reason ?? null },
      { params: { store_id: storeId } }
    );
    return response.data;
  },

  // 承認時のスキル付与（明示リスト方式・空可・冪等。skill_granted イベント発火）
  // 付録D.2 POST /api/recruiter/applications/{application_id}/approve-skills
  approveSkills: async (
    applicationId: string,
    skillDefinitionIds: string[],
    token: string
  ): Promise<ApproveSkillsResponse> => {
    const response = await apiClient.post<ApproveSkillsResponse>(
      `/recruiter/applications/${applicationId}/approve-skills`,
      { skill_definition_ids: skillDefinitionIds },
      { params: { token } }
    );
    return response.data;
  }
};

// 店舗情報関連のAPI
export const storeApi = {
  // 店舗情報取得
  getStoreInfo: async (storeId: string | null): Promise<StoreInfo> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    const response = await apiClient.get<StoreInfo>(`/store/`, { 
      params: { store_id: storeId } 
    });
    return response.data;
  },
  
  // 全店舗情報取得
  getAllStores: async (): Promise<{ id: string, name: string, address: string }[]> => {
    const response = await apiClient.get<{ id: string, name: string, address: string }[]>('/store/all');
    return response.data;
  },

  // 店舗情報更新
  updateStoreInfo: async (storeId: string, storeData: StoreUpdateForm): Promise<StoreInfo> => {
    const response = await apiClient.put<StoreInfo>(`/store/${storeId}`, storeData);
    return response.data;
  },
  
  // 店舗画像アップロード
  uploadStoreImage: async (storeId: string, file: File): Promise<StoreInfo> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await apiClient.post<StoreInfo>(`/store/${storeId}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },
  
  // 店舗画像削除
  deleteStoreImage: async (storeId: string): Promise<StoreInfo> => {
    const response = await apiClient.delete<StoreInfo>(`/store/${storeId}/image`);
    return response.data;
  },
  
  // 単一画像アップロード（汎用）
  uploadImage: async (file: File, folder: string = 'store-images'): Promise<ImageUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await apiClient.post<ImageUploadResponse>('/store/images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      params: { folder }
    });
    return response.data;
  },

  // 運営店舗チェック
  checkAdmin: async (storeId: string, adminEmail: string): Promise<boolean> => {
    const response = await apiClient.get<boolean>('/store/check-admin', {
      params: { store_id: storeId, admin_email: adminEmail }
    });
    return response.data;
  }
};

// お知らせ関連のAPI
export const announcementApi = {
  // 店舗向けお知らせ一覧取得
  getStoreAnnouncements: async (
    storeId: string,
    params: {
      skip?: number;
      limit?: number;
      category_id?: string;
      only_unread?: boolean;
      sort_order?: 'asc' | 'desc';
      sort_by?: 'created_at' | 'start_date';
      days?: number;
    } = {}
  ): Promise<Announcement[]> => {
    const queryParams = {
      skip: params.skip || 0,
      limit: params.limit || 20,
      ...(params.category_id && { category_id: params.category_id }),
      ...(params.only_unread !== undefined && { only_unread: params.only_unread }),
      ...(params.sort_order && { sort_order: params.sort_order }),
      ...(params.sort_by && { sort_by: params.sort_by }),
      ...(params.days && { days: params.days })
    };

    const response = await apiClient.get<Announcement[]>(`/announcements/store/${storeId}/announcements`, {
      params: queryParams
    });
    return response.data;
  },

  // 未読数取得
  getUnreadCount: async (storeId: string): Promise<UnreadCountResponse> => {
    const response = await apiClient.get<UnreadCountResponse>(`/announcements/store/${storeId}/announcements/unread/count`);
    return response.data;
  },

  // お知らせ詳細取得
  getAnnouncementDetail: async (announcementId: string, userId?: string, storeId?: string | null): Promise<AnnouncementDetail> => {
    const response = await apiClient.get<AnnouncementDetail>(`/announcements/announcements/${announcementId}`, {
      params:{
        user_id: userId,
        store_id: storeId
    }});
    return response.data;
  },

  // 既読マーク
  markAsRead: async (announcementId: string, userId?: string, storeId?: string): Promise<{ status: string; message: string }> => {
    const response = await apiClient.post<{ status: string; message: string }>(`/announcements/announcements/${announcementId}/read`, {
      params:{user_id: userId, store_id: storeId}
    });
    return response.data;
  },

  // 一括既読マーク
  markAllAsRead: async (storeId: string): Promise<{ status: string; marked_count: number; message: string }> => {
    const response = await apiClient.post<{ status: string; marked_count: number; message: string }>(`/announcements/store/${storeId}/announcements/read/all`);
    return response.data;
  },

  // カテゴリ一覧取得
  getCategories: async (): Promise<AnnouncementCategory[]> => {
    const response = await apiClient.get<AnnouncementCategory[]>('/announcements/categories');
    return response.data;
  }
};

// 店舗通知ID管理API
export const storeNotificationApi = {
  // 店舗の通知ID設定一覧取得
  getAll: async (storeId: string | null, params = {}): Promise<StoreNotificationIdList> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }

    const queryParams = { ...params, store_id: storeId };
    const response = await apiClient.get<StoreNotificationIdList>('/store-notification/', { params: queryParams });
    return response.data;
  },

  // 特定の通知ID設定取得（UUID指定）
  getById: async (id: string): Promise<StoreNotificationId> => {
    const response = await apiClient.get<StoreNotificationId>(`/store-notification/${id}`);
    return response.data;
  },

  // 店舗IDと通知IDで特定の設定取得
  getByStoreAndNotificationId: async (storeId: string, notificationId: string): Promise<StoreNotificationId> => {
    const response = await apiClient.get<StoreNotificationId>(`/store-notification/by-store/${storeId}/notification/${notificationId}`);
    return response.data;
  },

  // 通知ID設定作成
  create: async (data: StoreNotificationIdCreate): Promise<StoreNotificationId> => {
    const response = await apiClient.post<StoreNotificationId>('/store-notification/', data);
    return response.data;
  },

  // 通知ID設定更新
  update: async (id: string, data: StoreNotificationIdUpdate): Promise<StoreNotificationId> => {
    const response = await apiClient.put<StoreNotificationId>(`/store-notification/${id}`, data);
    return response.data;
  },

  // 通知ID設定削除
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/store-notification/${id}`);
  }
};

// Identity Platform ユーザー管理API
export const adminIdentityUserApi = {
  // 店舗のユーザー一覧取得
  getStoreUsers: async (storeId: string): Promise<IdentityUser[]> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    
    const response = await apiClient.get<IdentityUser[]>(`/admin/identity-user/${storeId}`, {
      headers: await operatorAuthHeader(),
    });
    return response.data;
  },

  // ユーザー作成（Identity Platform + Custom Claim）
  // 課題111: isCompanyAdmin=true のとき、企業管理者（ManagementAccount + CompanyManagementAccount）も作成する。
  // その場合は管理JWT(token)をクエリで送り、バックエンドが require_superadmin で運営者権限を検証する。
  create: async (userData: {
    email: string;
    displayName?: string;
    storeId: string;
    isCompanyAdmin?: boolean;
    role?: 'owner' | 'manager' | 'viewer';
    token?: string;
  }): Promise<IdentityUser> => {
    const { email, displayName, storeId, isCompanyAdmin, role, token } = userData;

    if (!email || !storeId) {
      throw new Error('必要なパラメータが不足しています');
    }

    // 推奨の一括作成エンドポイントを使用（パスワードはバックエンドで自動生成される）
    const response = await apiClient.post<IdentityUser>('/admin/identity-user/create-with-store',
      {
        email,
        ...(displayName ? { display_name: displayName } : {}),
        ...(isCompanyAdmin ? { is_company_admin: true, role: role || 'manager' } : {}),
      },
      {
        params: { store_id: storeId, ...(isCompanyAdmin && token ? { token } : {}) },
        headers: await operatorAuthHeader(),
      }
    );
    return response.data;
  },

  // ユーザー詳細情報取得
  getDetail: async (uid: string): Promise<IdentityUserDetail> => {
    if (!uid) {
      throw new Error('ユーザーUIDが必要です');
    }

    const response = await apiClient.get<IdentityUserDetail>(`/admin/identity-user/detail/${uid}`, {
      headers: await operatorAuthHeader(),
    });
    return response.data;
  },

  // ユーザー情報更新（アカウント有効/無効の切替のみ。課題122: パスワード直接再設定は廃止）
  update: async (uid: string, userData: { disabled?: boolean }, storeId: string): Promise<IdentityUser> => {
    if (!uid || !storeId) {
      throw new Error('ユーザーUIDと店舗IDが必要です');
    }

    const response = await apiClient.put<IdentityUser>(`/admin/identity-user/${uid}`, userData, {
      params: { store_id: storeId },
      headers: await operatorAuthHeader(),
    });
    return response.data;
  },

  // アカウント無効化/有効化
  toggleStatus: async (uid: string, disabled: boolean, storeId: string): Promise<IdentityUser> => {
    return adminIdentityUserApi.update(uid, { disabled }, storeId);
  },

  // ユーザー削除
  delete: async (uid: string, storeId: string): Promise<{ message: string; deleted_uid: string }> => {
    if (!uid || !storeId) {
      throw new Error('ユーザーUIDと店舗IDが必要です');
    }

    const response = await apiClient.delete(`/admin/identity-user/${uid}`, {
      params: { store_id: storeId },
      headers: await operatorAuthHeader(),
    });
    return response.data;
  }
};

// 運営店舗用の店舗API
export const adminStoreApi = {
  // 全店舗情報取得
  getAll: async (): Promise<AdminStoreList[]> => {
    const response = await apiClient.get<AdminStoreList[]>('/admin/store/', {
      headers: await operatorAuthHeader(),
    });
    return response.data;
  },

  // 店舗新規作成
  createStore: async (storeData: AdminStoreCreateForm): Promise<AdminStore> => {
    const response = await apiClient.post<AdminStore>('/admin/store/', storeData, {
      headers: await operatorAuthHeader(),
    });
    return response.data;
  },

  // 店舗情報更新
  updateStore: async (storeId: string, storeData: AdminStoreUpdateForm): Promise<AdminStore> => {
    const response = await apiClient.put<AdminStore>(`/admin/store/${storeId}`, storeData, {
      headers: await operatorAuthHeader(),
    });
    return response.data;
  },

  // 店舗詳細取得
  getStoreDetail: async (storeId: string): Promise<AdminStoreDetail> => {
    const response = await apiClient.get<AdminStoreDetail>(`/admin/store/${storeId}`, {
      headers: await operatorAuthHeader(),
    });
    return response.data;
  },

  // 初期設定可否判定
  getInitialSetupStatus: async (storeId: string): Promise<AdminStoreInitialSetupStatus> => {
    const response = await apiClient.get<AdminStoreInitialSetupStatus>(`/admin/store/${storeId}/initial-setup-status`, {
      headers: await operatorAuthHeader(),
    });
    return response.data;
  },

  // 店舗削除
  deleteStore: async (storeId: string): Promise<void> => {
    await apiClient.delete(`/admin/store/${storeId}`, {
      headers: await operatorAuthHeader(),
    });
  },

  // 初期登録用店舗ID発行
  reserveInitialSetup: async (data: { name: string; address: string; phone: string; email: string; company_id?: string; new_company_name?: string }): Promise<{ store_id: string; liff_id: string; message: string }> => {
    const response = await apiClient.post<{ store_id: string; liff_id: string; message: string }>('/admin/store/initial-setup-reserve', data, {
      headers: await operatorAuthHeader(),
    });
    return response.data;
  },

  // パスワードリセットメール送信
  passwordReset: async (email: string): Promise<{ message: string; email: string }> => {
    const response = await apiClient.post<{ message: string; email: string }>('/admin/store/password-reset', { email });
    return response.data;
  },

  // LINE認証情報の重複チェック（保存前の事前検証）
  checkLineCredentials: async (data: {
    line_channel_access_token?: string;
    line_channel_secret?: string;
    exclude_store_id?: string;
  }): Promise<{
    is_available: boolean;
    conflicting_field?: 'line_channel_access_token' | 'line_channel_secret' | null;
    message?: string | null;
  }> => {
    const response = await apiClient.post<{
      is_available: boolean;
      conflicting_field?: 'line_channel_access_token' | 'line_channel_secret' | null;
      message?: string | null;
    }>('/admin/store/check-line-credentials', data, {
      headers: await operatorAuthHeader(),
    });
    return response.data;
  }
};

// 運営用 企業選択肢API（店舗作成時の企業選択に使用）
export const adminCompanyApi = {
  // 有効企業一覧取得
  getAll: async (): Promise<AdminCompanyOption[]> => {
    const response = await apiClient.get<AdminCompanyOption[]>('/admin/company/', {
      headers: managementAuthHeader()
    });
    return response.data;
  },
};

// 運営店舗お知らせAPI
export const adminAnnouncementsApi = {
  // お知らせ一覧取得
  getAll: async (params: {
    page?: number;
    per_page?: number;
    category_id?: string;
    is_published?: boolean;
    target_type?: string;
    search?: string;
    sort_by?: string;
    sort_order?: string;
  } = {}): Promise<AdminAnnouncementListResponse> => {
    const queryParams = {
      page: params.page || 1,
      per_page: params.per_page || 20,
      ...(params.category_id && { category_id: params.category_id }),
      ...(params.is_published !== undefined && { is_published: params.is_published }),
      ...(params.target_type && { target_type: params.target_type }),
      ...(params.search && { search: params.search }),
      ...(params.sort_by && { sort_by: params.sort_by }),
      ...(params.sort_order && { sort_order: params.sort_order })
    };

    const response = await apiClient.get<AdminAnnouncementListResponse>('/admin/announcements/', {
      params: queryParams,
      headers: managementAuthHeader()
    });
    return response.data;
  },

  // お知らせ詳細取得
  getById: async (announcementId: string): Promise<AdminAnnouncementResponse> => {
    const response = await apiClient.get<AdminAnnouncementResponse>(`/admin/announcements/${announcementId}`, {
      headers: managementAuthHeader()
    });
    return response.data;
  },

  // お知らせ作成
  create: async (data: AdminAnnouncementCreateRequest): Promise<AdminAnnouncementResponse> => {
    const response = await apiClient.post<AdminAnnouncementResponse>('/admin/announcements/', data, {
      headers: managementAuthHeader()
    });
    return response.data;
  },

  // お知らせ更新
  update: async (announcementId: string, data: AdminAnnouncementUpdateRequest): Promise<AdminAnnouncementResponse> => {
    const response = await apiClient.put<AdminAnnouncementResponse>(`/admin/announcements/${announcementId}`, data, {
      headers: managementAuthHeader()
    });
    return response.data;
  },

  // お知らせ削除
  delete: async (announcementId: string): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ message: string }>(`/admin/announcements/${announcementId}`, {
      headers: managementAuthHeader()
    });
    return response.data;
  },

  // カテゴリ一覧取得
  getCategories: async (): Promise<AnnouncementCategory[]> => {
    const response = await apiClient.get<AnnouncementCategory[]>('/admin/announcements/categories/', {
      headers: managementAuthHeader()
    });
    return response.data;
  }
};

export const faqApi = {
  // FAQ一覧取得
  getAll: async (): Promise<FAQ[]> => {
    const response = await apiClient.get<FAQ[]>('/system-settings/faqs');
    return response.data;
  },

  // 管理画面用：全FAQ取得（非アクティブも含む）
  getAllForAdmin: async (): Promise<FAQ[]> => {
    const response = await apiClient.get<FAQ[]>('/system-settings/faqs/admin/all');
    return response.data;
  },

  // 特定のFAQ取得
  getById: async (id: string): Promise<FAQ> => {
    const response = await apiClient.get<FAQ>(`/system-settings/faqs/${id}`);
    return response.data;
  },

  // FAQ作成
  create: async (data: {
    question: string;
    answer: string;
    icon_name?: string;
    has_action?: boolean;
    display_order?: number;
    is_active?: boolean;
  }): Promise<FAQ> => {
    const response = await apiClient.post<FAQ>('/system-settings/faqs', data);
    return response.data;
  },

  // FAQ更新
  update: async (id: string, data: FAQUpdate): Promise<FAQ> => {
    const response = await apiClient.put<FAQ>(`/system-settings/faqs/${id}`, data);
    return response.data;
  },

  // FAQ有効/無効切り替え
  toggleActive: async (id: string): Promise<{ message: string }> => {
    const response = await apiClient.patch<{ message: string }>(`/system-settings/faqs/${id}/toggle-active`);
    return response.data;
  }
};

// 勤怠管理API
const attendanceApi = {
  // 出勤打刻
  checkIn: async (storeUserId: string, request: {
    qr_payload: string;
    device_info?: string;
  }) => {
    const response = await apiClient.post(`/attendance/check-in?store_user_id=${storeUserId}`, request);
    return response.data;
  },

  // 退勤打刻
  checkOut: async (storeUserId: string, request: {
    qr_payload: string;
    device_info?: string;
  }) => {
    const response = await apiClient.post(`/attendance/check-out?store_user_id=${storeUserId}`, request);
    return response.data;
  },

  // 出勤履歴取得
  getHistory: async (storeUserId: string, limit: number = 50, offset: number = 0) => {
    const response = await apiClient.get(`/attendance/history/${storeUserId}?limit=${limit}&offset=${offset}`);
    return response.data;
  },

  // 本日の勤務状況取得
  getCurrent: async (storeUserId: string) => {
    const response = await apiClient.get(`/attendance/current/${storeUserId}`);
    return response.data;
  },

  // 店舗QRコード取得
  getStoreQR: async (storeId: string) => {
    const response = await apiClient.get(`/attendance/store-qr/${storeId}`);
    return response.data;
  },

  // 店舗QRコード生成
  generateStoreQR: async (storeId: string, validityDays: number = 365) => {
    const response = await apiClient.post(`/attendance/generate-store-qr`, null, {
      params: { store_id: storeId, validity_days: validityDays }
    });
    return response.data;
  },

  // 承認待ちリスト取得
  getApprovalList: async (storeId: string) => {
    const response = await apiClient.get(`/attendance/approval-list/${storeId}`);
    return response.data;
  },

  // 課題8/114: 承認済み勤怠一覧（承認取消UI用）。cancellable=false は引出済みで取消不可。
  getVerifiedList: async (storeId: string) => {
    const response = await apiClient.get(`/attendance/verified-list/${storeId}`);
    return response.data;
  },

  // 課題8/114: 承認取消（verified → pending）。未引出・未締めのみ可（それ以外は409）。
  cancelVerification: async (
    workAttendanceId: string,
    storeId: string,
    managerId: string,
    reason: string
  ) => {
    const response = await apiClient.post(
      `/attendance/verify/${workAttendanceId}/cancel`,
      { reason },
      { params: { store_id: storeId, manager_id: managerId } }
    );
    return response.data;
  },

  // 勤怠承認/却下
  verifyAttendance: async (
    workAttendanceId: string,
    managerId: string,
    approve: boolean,
    verificationNotes?: string
  ) => {
    const response = await apiClient.post(`/attendance/verify/${workAttendanceId}`, null, {
      params: {
        manager_id: managerId,
        approve: approve,
        verification_notes: verificationNotes
      }
    });
    return response.data;
  },

  // 勤怠時刻修正（飛び込み出勤の時給・休憩入力も兼ねる）
  updateAttendanceTime: async (
    workAttendanceId: string,
    checkInTime?: string,
    checkOutTime?: string,
    hourlyWage?: number,
    // 休憩時間は勤務種別を問わず送信する（シフト定義の休憩を上書きする）。
    // null を明示送信するとサーバ側で上書きが解除され、シフト定義の休憩に戻る。
    // undefined は JSON 化時にキーごと落ちる＝「変更しない」。
    breakMinutes?: number | null
  ) => {
    const response = await apiClient.patch(`/attendance/update-time/${workAttendanceId}`, {
      check_in_time: checkInTime,
      check_out_time: checkOutTime,
      hourly_wage: hourlyWage,
      break_minutes: breakMinutes
    });
    return response.data;
  }
};

// ウォレット管理API
// Q14: store-user wallet エンドポイントは管理JWT＋店舗所有権が必須になった。
// 管理系API規約に合わせ token を引数で受け取り params 経由で送る（呼び出し側は
// ManagementAuthContext の management_token を渡す）。
const walletApi = {
  // 残高照会
  getBalance: async (storeUserId: string, token: string) => {
    const response = await apiClient.get(`/wallet/balance/${storeUserId}`, { params: { token } });
    return response.data;
  },

  // 取引履歴取得
  getTransactions: async (storeUserId: string, token: string, limit: number = 50, offset: number = 0) => {
    const response = await apiClient.get(`/wallet/transactions/${storeUserId}`, {
      params: { limit, offset, token },
    });
    return response.data;
  },

  // 引き出し申請作成
  createWithdrawalRequest: async (storeUserId: string, requestedAmount: number, token: string) => {
    const response = await apiClient.post(`/wallet/withdrawal-request/${storeUserId}`, null, {
      params: { requested_amount: requestedAmount, token },
    });
    return response.data;
  },

  // ウォレット統計情報取得
  getStatistics: async (storeUserId: string, token: string, days?: number) => {
    const response = await apiClient.get(`/wallet/statistics/${storeUserId}`, {
      params: days ? { days, token } : { token },
    });
    return response.data;
  },

  // ハッシュチェーン検証
  verifyChain: async (storeUserId: string, token: string) => {
    const response = await apiClient.get(`/wallet/verify-chain/${storeUserId}`, { params: { token } });
    return response.data;
  }
};

// 利用明細API
const billingApi = {
  // 月次利用明細一覧取得
  getRecords: async (storeId: string, months: number = 12): Promise<BillingRecordsResponse> => {
    const response = await apiClient.get<BillingRecordsResponse>(`/billing/${storeId}`, { params: { months } });
    return response.data;
  },
  // 月次利用明細CSVダウンロード
  downloadCsv: async (storeId: string, year: number, month: number): Promise<Blob> => {
    const response = await apiClient.get(`/billing/${storeId}/download/${year}/${month}`, {
      responseType: 'blob',
    });
    return response.data;
  },
  // 請求額調整明細CSVダウンロード（確定済み月のみ）
  downloadAdjustmentCsv: async (storeId: string, year: number, month: number): Promise<Blob> => {
    const response = await apiClient.get(`/billing/${storeId}/download/${year}/${month}/adjustment`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// 管理アカウント認証API
//
// ログイン系2本は Firebase ID トークンを Authorization ヘッダーで送る。
// パスワードの実質的な検証は Firebase(Identity Platform) が行っており、
// バックエンドは「Firebase 認証を通った本人か」をこのトークンで確認して
// アプリ内JWTを発行する。以前はトークンを送っておらず、バックエンド側も
// 検証していなかったため、メールアドレスを知る誰でも運営者としてログインできた。
// 運営APIに管理JWTを渡すためのヘルパー。
//
// バックエンドの運営系エンドポイント（承認・お知らせ・即給疎通）は
// `token` クエリパラメータで管理JWT(superadmin)を受け取る規約になっている。
// 呼び出し側の関数シグネチャを変えずに済むよう、ここで localStorage から読む。
// （既存の wallet 系などは呼び出し元が token を明示的に渡す書き方だが、
//   運営APIは全画面から呼ばれるため、引数追加の波及を避けてこの形にする）
const managementToken = (): string => {
  try {
    return localStorage.getItem(MANAGEMENT_TOKEN_KEY) ?? '';
  } catch {
    // プライベートモード等で localStorage が例外を投げる場合は空文字。
    // バックエンドが 401 を返し、インターセプタがログイン画面へ誘導する。
    return '';
  }
};

// 管理JWTを Authorization ヘッダーで送る。
//
// 以前はクエリ `?token=` で送っていたが、クエリ文字列はアクセスログ・
// ブラウザ履歴・Referer に平文で残る。管理JWTは30日有効で失効手段が
// アカウント無効化しかないため、露出経路を減らす目的でヘッダーへ移した。
// バックエンドは移行期の互換としてクエリも受けるが、警告ログを出す。
const managementAuthHeader = (): Record<string, string> => {
  const t = managementToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// 呼び出し元が管理JWTを明示的に渡す既存APIを、クエリからヘッダーへ移すための変換。
//
// 空文字のときヘッダーを付けないのが重要。店舗管理（Firebase ログイン）ユーザーは
// 管理JWTを持たず空文字を渡してくるが、バックエンド側はこれを
// 「所有権チェックをスキップする店舗ログイン経路」として正規に扱う
// （app/api/gamification/auth.py: verify_store_ownership）。
// 空の Authorization ヘッダーを送ると 401 になり、この経路が壊れる。
const bearerHeader = (token: string): Record<string, string> =>
  token ? { Authorization: `Bearer ${token}` } : {};

// 店舗管理API（/admin/store, /admin/identity-user）用の認証ヘッダー。
//
// これらのAPIは2経路から呼ばれる:
//   - 運営メニュー / 企業管理  … 管理JWT を持つ（localStorage）
//   - 店舗ログイン（Firebase）… 管理JWT は発行されない。Firebase ID トークンのみ
// 同じ api.ts の関数が両方から呼ばれるため、持っている資格情報を優先順で載せる。
// バックエンド（app/api/admin/token_source.py: require_operator_dep）は署名
// アルゴリズム（HS256=管理JWT / RS256=Firebase）で振り分けて検証する。
//
// 注意: ログイン前フロー（パスワードリセット）では両方とも無いのでこれを使わない。
const operatorAuthHeader = async (): Promise<Record<string, string>> => {
  const t = managementToken();
  if (t) {
    return { Authorization: `Bearer ${t}` };
  }
  return firebaseAuthHeader();
};

const firebaseAuthHeader = async (): Promise<Record<string, string>> => {
  // login.tsx が signInWithEmailAndPassword を済ませた直後に呼ばれるため
  // currentUser は存在する。念のため無い場合は明示的に失敗させる
  // （ヘッダー無しで送るとバックエンドが 401 を返し、原因が分かりにくい）。
  const user = getAuth(getApp()).currentUser;
  if (!user) {
    throw new Error('Firebase認証が完了していません');
  }
  return { Authorization: `Bearer ${await user.getIdToken()}` };
};

export const managementAuthApi = {
  // 管理アカウントでログイン（Firebase認証済み前提）
  login: async (data: ManagementAccountLoginRequest): Promise<ManagementAccountLoginResponse> => {
    const response = await apiClient.post<ManagementAccountLoginResponse>(
      '/management/auth/login', data, { headers: await firebaseAuthHeader() }
    );
    return response.data;
  },

  // メールアドレスで自動ログイン（DBパスワード不要・Firebase認証済み前提）
  loginByEmail: async (email: string): Promise<ManagementAccountLoginResponse> => {
    const response = await apiClient.post<ManagementAccountLoginResponse>('/management/auth/login-by-email', null, {
      params: { email },
      headers: await firebaseAuthHeader(),
    });
    return response.data;
  },

  // 現在のアカウント情報取得
  getMe: async (token: string): Promise<ManagementAccount> => {
    const response = await apiClient.get<ManagementAccount>('/management/auth/me', {
      params: { token }
    });
    return response.data;
  },

  // アカウント情報更新
  updateMe: async (token: string, data: ManagementAccountUpdate): Promise<ManagementAccount> => {
    const response = await apiClient.put<ManagementAccount>('/management/auth/me', data, {
      params: { token }
    });
    return response.data;
  },

  // メール受信設定取得
  getEmailSettings: async (token: string): Promise<EmailSetting[]> => {
    const response = await apiClient.get<EmailSetting[]>('/management/auth/email-settings', {
      params: { token }
    });
    return response.data;
  },

  // メール受信設定更新
  updateEmailSettings: async (token: string, settings: EmailSettingUpdate[]): Promise<EmailSetting[]> => {
    const response = await apiClient.put<EmailSetting[]>('/management/auth/email-settings', settings, {
      params: { token }
    });
    return response.data;
  },
};

// 企業管理API
export const managementCompanyApi = {
  // 企業一覧取得
  getAll: async (token: string): Promise<CompanyDetail[]> => {
    const response = await apiClient.get<CompanyDetail[]>('/management/companies/', {
      params: { token }
    });
    return response.data;
  },

  // 企業詳細取得
  getById: async (companyId: string, token: string): Promise<CompanyDetail> => {
    const response = await apiClient.get<CompanyDetail>(`/management/companies/${companyId}`, {
      params: { token }
    });
    return response.data;
  },

  // 企業情報更新
  update: async (companyId: string, token: string, data: CompanyUpdate): Promise<CompanyDetail> => {
    const response = await apiClient.put<CompanyDetail>(`/management/companies/${companyId}`, data, {
      params: { token }
    });
    return response.data;
  },

  // 企業の店舗一覧取得
  getStores: async (companyId: string, token: string): Promise<StoreBrief[]> => {
    const response = await apiClient.get<StoreBrief[]>(`/management/companies/${companyId}/stores`, {
      params: { token }
    });
    return response.data;
  },

  // 利用明細一覧取得
  getBillingRecords: async (companyId: string, token: string, page: number = 1, perPage: number = 10): Promise<BillingRecord[]> => {
    const response = await apiClient.get<BillingRecord[]>(`/management/companies/${companyId}/billing`, {
      params: { token, page, per_page: perPage }
    });
    return response.data;
  },

  // 利用明細件数取得
  getBillingCount: async (companyId: string, token: string): Promise<{ total: number }> => {
    const response = await apiClient.get<{ total: number }>(`/management/companies/${companyId}/billing/count`, {
      params: { token }
    });
    return response.data;
  },

  // 利用明細詳細取得
  getBillingDetail: async (companyId: string, recordId: string, token: string): Promise<BillingRecordWithDetails> => {
    const response = await apiClient.get<BillingRecordWithDetails>(`/management/companies/${companyId}/billing/${recordId}`, {
      params: { token }
    });
    return response.data;
  },

  // 利用明細CSVダウンロード
  downloadBillingCsv: async (companyId: string, recordId: string, downloadType: string, token: string): Promise<Blob> => {
    const response = await apiClient.get(`/management/companies/${companyId}/billing/${recordId}/download/${downloadType}`, {
      params: { token },
      responseType: 'blob'
    });
    return response.data;
  },

  // 労働者名簿CSVダウンロード
  downloadWorkersCsv: async (companyId: string, token: string, startDate?: string, endDate?: string): Promise<Blob> => {
    const response = await apiClient.get(`/management/companies/${companyId}/workers/download`, {
      params: { token, start_date: startDate, end_date: endDate },
      responseType: 'blob'
    });
    return response.data;
  },

  // 企業登録申請
  register: async (data: ManagementRequestCreate, options: { token?: string }): Promise<ManagementRequestResponse> => {
    const params: Record<string, string> = {};
    if (options.token) params.token = options.token;
    const response = await apiClient.post<ManagementRequestResponse>('/management/companies/register', data, {
      params
    });
    return response.data;
  },

  // 企業登録申請状況確認
  getRegistrationStatus: async (requestId: string, token: string): Promise<ManagementRequestResponse> => {
    const response = await apiClient.get<ManagementRequestResponse>(`/management/companies/registration-status/${requestId}`, {
      params: { token }
    });
    return response.data;
  },

  // 自分の企業登録申請一覧
  getMyRegistrations: async (options: { token?: string; email?: string }): Promise<ManagementRequestResponse[]> => {
    const params: Record<string, string> = {};
    if (options.token) params.token = options.token;
    if (options.email) params.email = options.email;
    const response = await apiClient.get<ManagementRequestResponse[]>('/management/companies/my-registrations', {
      params
    });
    return response.data;
  },

  // 店舗追加申請
  requestStoreAddition: async (companyId: string, token: string, data: ManagementRequestCreate): Promise<ManagementRequestResponse> => {
    const response = await apiClient.post<ManagementRequestResponse>(`/management/companies/${companyId}/stores/request`, data, {
      params: { token }
    });
    return response.data;
  },

  // 店舗追加申請一覧取得
  getStoreRequests: async (companyId: string, token: string): Promise<ManagementRequestResponse[]> => {
    const response = await apiClient.get<ManagementRequestResponse[]>(`/management/companies/${companyId}/store-requests`, {
      params: { token }
    });
    return response.data;
  },

  // 店舗解約予約
  scheduleStoreClosing: async (companyId: string, storeId: string, token: string, data?: StoreClosingCreate): Promise<StoreClosingResponse> => {
    const response = await apiClient.post<StoreClosingResponse>(`/management/companies/${companyId}/stores/${storeId}/close`, data || {}, {
      params: { token }
    });
    return response.data;
  },

  // 店舗解約予約キャンセル
  cancelStoreClosing: async (companyId: string, storeId: string, token: string): Promise<void> => {
    await apiClient.delete(`/management/companies/${companyId}/stores/${storeId}/close`, {
      params: { token }
    });
  },

  // 解約予約一覧取得
  getClosingSchedules: async (companyId: string, token: string): Promise<StoreClosingResponse[]> => {
    const response = await apiClient.get<StoreClosingResponse[]>(`/management/companies/${companyId}/closing-schedules`, {
      params: { token }
    });
    return response.data;
  },
};

// 店舗新規申込API
const storeApplicationApi = {
  /** 店舗申込実行（公開・認証不要） */
  create: async (data: StoreApplicationCreate): Promise<StoreApplication> => {
    const response = await apiClient.post<StoreApplication>('/store-applications', data);
    return response.data;
  },

  /** 店舗申込一覧取得（管理画面用） */
  getAll: async (status?: StoreApplicationStatus): Promise<StoreApplication[]> => {
    const response = await apiClient.get<StoreApplication[]>('/store-applications', {
      params: { status }
    });
    return response.data;
  },

  /** 店舗申込ステータス更新（管理画面用） */
  updateStatus: async (
    id: string,
    data: { status: StoreApplicationStatus; notes?: string },
  ): Promise<StoreApplication> => {
    const response = await apiClient.put<StoreApplication>(
      `/store-applications/${id}/status`,
      data,
    );
    return response.data;
  },
};

// フィードバック（ご意見・ご要望）API
export const feedbackApi = {
  submit: async (data: {
    inquiry_type: string;
    company_name: string;
    store_name: string;
    email: string;
    message: string;
  }): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>('/feedback', data);
    return response.data;
  },
};

export const dashboardApi = {
  getFeeComparison: async (storeId: string, year?: number): Promise<FeeComparisonResponse> => {
    const params = year !== undefined ? { year } : {};
    const response = await apiClient.get<FeeComparisonResponse>(`/dashboard/fee-comparison/${storeId}`, { params });
    return response.data;
  },
};

// 運営承認API（管理者用）
export const adminApprovalApi = {
  // 企業登録申請一覧取得
  getCompanyRequests: async (status?: string): Promise<ManagementRequestResponse[]> => {
    const response = await apiClient.get<ManagementRequestResponse[]>('/admin/approval/company-requests', {
      params: status ? { status } : {},
      headers: managementAuthHeader()
    });
    return response.data;
  },

  // 企業登録申請詳細取得
  getCompanyRequestDetail: async (requestId: string): Promise<ManagementRequestResponse> => {
    const response = await apiClient.get<ManagementRequestResponse>(`/admin/approval/company-requests/${requestId}`, {
      headers: managementAuthHeader()
    });
    return response.data;
  },

  // 企業登録申請承認
  approveCompanyRequest: async (requestId: string, reviewedBy?: string): Promise<{ message: string; company_id: string }> => {
    const response = await apiClient.post<{ message: string; company_id: string }>(`/admin/approval/company-requests/${requestId}/approve`, {
      reviewed_by: reviewedBy
    }, { headers: managementAuthHeader() });
    return response.data;
  },

  // 企業登録申請却下
  rejectCompanyRequest: async (requestId: string, reason: string, reviewedBy?: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(`/admin/approval/company-requests/${requestId}/reject`, {
      reason,
      reviewed_by: reviewedBy
    }, { headers: managementAuthHeader() });
    return response.data;
  },

  // 店舗追加申請一覧取得
  getStoreRequests: async (status?: string): Promise<ManagementRequestResponse[]> => {
    const response = await apiClient.get<ManagementRequestResponse[]>('/admin/approval/store-requests', {
      params: status ? { status } : {},
      headers: managementAuthHeader()
    });
    return response.data;
  },

  // 店舗追加申請承認
  approveStoreRequest: async (requestId: string, reviewedBy?: string): Promise<{ message: string; store_id: string }> => {
    const response = await apiClient.post<{ message: string; store_id: string }>(`/admin/approval/store-requests/${requestId}/approve`, {
      reviewed_by: reviewedBy
    }, { headers: managementAuthHeader() });
    return response.data;
  },

  // 店舗追加申請却下
  rejectStoreRequest: async (requestId: string, reason: string, reviewedBy?: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(`/admin/approval/store-requests/${requestId}/reject`, {
      reason,
      reviewed_by: reviewedBy
    }, { headers: managementAuthHeader() });
    return response.data;
  },
};

// 即給連携管理API（Phase 7）
export const adminSokyuApi = {
  // 全企業の即給連携設定一覧
  listConfigs: async (): Promise<import('../types').SokyuConfigRead[]> => {
    const response = await apiClient.get<import('../types').SokyuConfigRead[]>(
      '/admin/companies/sokyu-config',
      { headers: managementAuthHeader() },
    );
    return response.data;
  },

  // 企業の即給連携設定取得
  getConfig: async (companyId: string): Promise<import('../types').SokyuConfigRead> => {
    const response = await apiClient.get<import('../types').SokyuConfigRead>(
      `/admin/companies/${companyId}/sokyu-config`,
      { headers: managementAuthHeader() },
    );
    return response.data;
  },

  // 即給連携設定の投入・更新
  updateConfig: async (
    companyId: string,
    update: import('../types').SokyuConfigUpdate,
  ): Promise<import('../types').SokyuConfigRead> => {
    const response = await apiClient.put<import('../types').SokyuConfigRead>(
      `/admin/companies/${companyId}/sokyu-config`,
      update,
      { headers: managementAuthHeader() },
    );
    return response.data;
  },

  // 店舗ごとの事業所番号を更新
  updateStoreBranch: async (
    companyId: string,
    storeId: string,
    update: import('../types').StoreBranchUpdate,
  ): Promise<import('../types').SokyuConfigRead> => {
    const response = await apiClient.put<import('../types').SokyuConfigRead>(
      `/admin/companies/${companyId}/stores/${storeId}/sokyu-config`,
      update,
      { headers: managementAuthHeader() },
    );
    return response.data;
  },

  // 事業所番号が即給マスタに存在するか照合する（参照専用・保存はしない）。
  // マスタに無い値を保存すると、その店舗に所属するワーカー全員の口座登録・変更が失敗する。
  // 即給へ外部リクエストを発行するため運営者（superadmin）限定。management JWT を渡す。
  verifyBranchNo: async (
    companyId: string,
    sokyuBranchNo: string,
    token: string,
  ): Promise<import('../types').BranchVerifyResponse> => {
    const response = await apiClient.post<import('../types').BranchVerifyResponse>(
      `/admin/companies/${companyId}/sokyu-branch-verify`,
      { sokyu_branch_no: sokyuBranchNo },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  },

  // 即給連携を有効化
  enable: async (
    companyId: string,
    force: boolean = false,
  ): Promise<import('../types').SokyuConfigRead> => {
    const response = await apiClient.post<import('../types').SokyuConfigRead>(
      `/admin/companies/${companyId}/sokyu-enable`,
      { force },
      { headers: managementAuthHeader() },
    );
    return response.data;
  },

  // 即給連携を無効化
  disable: async (companyId: string): Promise<import('../types').SokyuConfigRead> => {
    const response = await apiClient.post<import('../types').SokyuConfigRead>(
      `/admin/companies/${companyId}/sokyu-disable`,
      undefined,
      { headers: managementAuthHeader() },
    );
    return response.data;
  },

  // 全企業の同期状況
  getSyncStatus: async (): Promise<import('../types').SokyuSyncStatusItem[]> => {
    const response = await apiClient.get<import('../types').SokyuSyncStatusItem[]>(
      '/admin/companies/sokyu-sync-status',
      { headers: managementAuthHeader() },
    );
    return response.data;
  },
};

// 運営（superadmin）向け 横断（総合）ランク設定 API（§5.2 / 付録D）。
// 全エンドポイントは management token をクエリ `token` で送る（バックエンド require_superadmin）。
export const adminGlobalRankApi = {
  // 係数設定取得 GET /api/admin/global-rank/config
  getConfig: async (token: string): Promise<import('../types').GlobalRankConfig> => {
    const response = await apiClient.get<import('../types').GlobalRankConfig>(
      '/admin/global-rank/config',
      { params: { token } },
    );
    return response.data;
  },

  // 係数設定更新 PUT /api/admin/global-rank/config
  updateConfig: async (
    token: string,
    payload: import('../types').GlobalRankConfigUpdate,
  ): Promise<import('../types').GlobalRankConfig> => {
    const response = await apiClient.put<import('../types').GlobalRankConfig>(
      '/admin/global-rank/config',
      payload,
      { params: { token } },
    );
    return response.data;
  },

  // tier別しきい値取得 GET /api/admin/global-rank/thresholds
  getThresholds: async (token: string): Promise<import('../types').GlobalRankThreshold[]> => {
    const response = await apiClient.get<import('../types').GlobalRankThreshold[]>(
      '/admin/global-rank/thresholds',
      { params: { token } },
    );
    return response.data;
  },

  // tier別しきい値更新 PUT /api/admin/global-rank/thresholds（単調性違反は 422）
  updateThresholds: async (
    token: string,
    thresholds: import('../types').GlobalRankThresholdUpdateItem[],
  ): Promise<import('../types').GlobalRankThreshold[]> => {
    const response = await apiClient.put<import('../types').GlobalRankThreshold[]>(
      '/admin/global-rank/thresholds',
      { thresholds },
      { params: { token } },
    );
    return response.data;
  },

  // 職種別重み取得 GET /api/admin/global-rank/position-weights
  getPositionWeights: async (token: string): Promise<import('../types').GlobalRankPositionWeight[]> => {
    const response = await apiClient.get<import('../types').GlobalRankPositionWeight[]>(
      '/admin/global-rank/position-weights',
      { params: { token } },
    );
    return response.data;
  },

  // 職種別重み一括更新 PUT /api/admin/global-rank/position-weights
  updatePositionWeights: async (
    token: string,
    weights: import('../types').GlobalRankPositionWeightUpdateItem[],
  ): Promise<import('../types').GlobalRankPositionWeight[]> => {
    const response = await apiClient.put<import('../types').GlobalRankPositionWeight[]>(
      '/admin/global-rank/position-weights',
      { weights },
      { params: { token } },
    );
    return response.data;
  },

  // 横断ランク即時再計算 POST /api/admin/global-rank/recalculate
  recalculate: async (
    token: string,
    payload: import('../types').GlobalRankRecalculateRequest = {},
  ): Promise<import('../types').GlobalRankRecalculateResponse> => {
    const response = await apiClient.post<import('../types').GlobalRankRecalculateResponse>(
      '/admin/global-rank/recalculate',
      payload,
      { params: { token } },
    );
    return response.data;
  },
};

// スキル設定API（ゲーミフィケーション・スキルカタログ CRUD / tier マスタ）
// 店長エンドポイント（§5.1）。store スコープのため store_id をパス、management token をクエリで送る。
export const skillSettingsApi = {
  // tier マスタ取得（rookie..gold の 5 行）
  // GET /api/store/{store_id}/skill-tiers
  getSkillTiers: async (storeId: string | null, token: string): Promise<SkillTier[]> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    const response = await apiClient.get<SkillTier[]>(`/store/${storeId}/skill-tiers`, {
      params: { token }
    });
    return response.data;
  },

  // スキルカタログ一覧取得（職種でフィルタ可。is_active のみ取得する場合は active_only=true）
  // GET /api/store/{store_id}/skill-definitions
  getSkillDefinitions: async (
    storeId: string | null,
    token: string,
    params: { position_id?: string; tier_id?: string; active_only?: boolean } = {}
  ): Promise<SkillDefinition[]> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    const response = await apiClient.get<SkillDefinition[]>(`/store/${storeId}/skill-definitions`, {
      params: { token, ...params }
    });
    return response.data;
  },

  // スキルカタログ作成（5/tier 上限はサーバが 422 で強制）
  // POST /api/store/{store_id}/skill-definitions
  createSkillDefinition: async (
    storeId: string | null,
    data: SkillDefinitionCreate,
    token: string
  ): Promise<SkillDefinition> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    const response = await apiClient.post<SkillDefinition>(`/store/${storeId}/skill-definitions`, data, {
      params: { token }
    });
    return response.data;
  },

  // スキルカタログ更新（名前・スロット位置・有効/無効）
  // PUT /api/store/{store_id}/skill-definitions/{skill_definition_id}
  updateSkillDefinition: async (
    storeId: string | null,
    skillDefinitionId: string,
    data: SkillDefinitionUpdate,
    token: string
  ): Promise<SkillDefinition> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    const response = await apiClient.put<SkillDefinition>(
      `/store/${storeId}/skill-definitions/${skillDefinitionId}`,
      data,
      { params: { token } }
    );
    return response.data;
  },

  // スキルカタログ削除（ソフトデリート＝is_active=false）
  // DELETE /api/store/{store_id}/skill-definitions/{skill_definition_id}
  deleteSkillDefinition: async (
    storeId: string | null,
    skillDefinitionId: string,
    token: string
  ): Promise<void> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    await apiClient.delete(`/store/${storeId}/skill-definitions/${skillDefinitionId}`, {
      params: { token }
    });
  },

  // スキルCSV一括登録用テンプレートのダウンロード
  // GET /api/store/{store_id}/skill-definitions/import-template
  downloadImportTemplate: async (storeId: string | null, token: string): Promise<Blob> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    const response = await apiClient.get(`/store/${storeId}/skill-definitions/import-template`, {
      params: { token },
      responseType: 'blob'
    });
    return response.data;
  },

  // スキルCSV一括登録（職種・ランク・スキル名・マニュアルURL。スロットは自動採番）
  // POST /api/store/{store_id}/skill-definitions/import-csv
  importSkillsCsv: async (
    storeId: string | null,
    file: File,
    token: string
  ): Promise<SkillImportResponse> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<SkillImportResponse>(
      `/store/${storeId}/skill-definitions/import-csv`,
      formData,
      {
        params: { token },
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  },

  // 横断スキル認識（050・表記ゆれ吸収）: 自店カタログの各スキルが他店舗で同義獲得済みかを取得
  // GET /api/store/{store_id}/cross-store-skills/{store_user_id}
  getCrossStoreSkills: async (
    storeId: string | null,
    storeUserId: string,
    token: string
  ): Promise<CrossStoreSkillsResponse> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    const response = await apiClient.get<CrossStoreSkillsResponse>(
      `/store/${storeId}/cross-store-skills/${storeUserId}`,
      { params: { token } }
    );
    return response.data;
  },

  // ===== 店舗ランク設定（旧051。2026-07-17に tier別閾値を店舗別へ再導入。2026-07-24に個数→達成率(%)へ変更） =====

  // 店舗ランク設定取得（勤務実績ゲート + tier別達成率(%)の実効値）  GET /api/store/{store_id}/rank-config
  getRankConfig: async (storeId: string | null, token: string): Promise<StoreRankConfig> => {
    if (!storeId) throw new Error('店舗IDが必要です');
    const response = await apiClient.get<StoreRankConfig>(`/store/${storeId}/rank-config`, { params: { token } });
    return response.data;
  },

  // 店舗ランク設定更新（勤務実績ゲート + tier別達成率(%)の全置換）  PUT /api/store/{store_id}/rank-config
  putRankConfig: async (
    storeId: string | null, data: StoreRankConfigUpdate, token: string
  ): Promise<StoreRankConfig> => {
    if (!storeId) throw new Error('店舗IDが必要です');
    const response = await apiClient.put<StoreRankConfig>(`/store/${storeId}/rank-config`, data, { params: { token } });
    return response.data;
  },
};

// 運営(superadmin)向け 正準スキル辞書 管理・是正API（050）。
// すべて /api/admin/canonical-skills/* を superadmin の management token で呼ぶ（require_superadmin）。
export const canonicalAdminApi = {
  // 正準スキル一覧（業種で絞り込み可・紐づく定義数つき）
  list: async (token: string, industryId?: string): Promise<CanonicalSkillAdminItem[]> => {
    const response = await apiClient.get<{ items: CanonicalSkillAdminItem[] }>(
      '/admin/canonical-skills/',
      { params: industryId ? { industry_id: industryId } : {}, headers: bearerHeader(token) }
    );
    return response.data.items;
  },

  // 正準スキル編集（名前/説明。変更時はサーバ側で再埋め込み）
  update: async (canonicalId: string, data: { name?: string; description?: string }, token: string) => {
    const response = await apiClient.put(`/admin/canonical-skills/${canonicalId}`, data, { headers: bearerHeader(token) });
    return response.data;
  },

  // 正準スキル統合（source → target）
  merge: async (sourceId: string, targetId: string, token: string) => {
    const response = await apiClient.post(
      '/admin/canonical-skills/merge',
      { source_id: sourceId, target_id: targetId },
      { headers: bearerHeader(token) }
    );
    return response.data;
  },

  // スキル定義を別の正準へ再マップ（分割/手動。manual固定）
  remap: async (
    payload: { skill_definition_id: string; target_canonical_id?: string; new_canonical_name?: string },
    token: string
  ) => {
    const response = await apiClient.post('/admin/canonical-skills/remap', payload, { headers: bearerHeader(token) });
    return response.data;
  },

  // スキル定義の正準化監査ログ
  audit: async (skillDefinitionId: string, token: string): Promise<CanonicalMappingAudit[]> => {
    const response = await apiClient.get<{ items: CanonicalMappingAudit[] }>(
      `/admin/canonical-skills/audit/${skillDefinitionId}`,
      { headers: bearerHeader(token) }
    );
    return response.data.items;
  },

  // 運用トリガ
  seed: async (token: string) => (await apiClient.post('/admin/canonical-skills/seed', null, { headers: bearerHeader(token) })).data,
  backfill: async (token: string) => (await apiClient.post('/admin/canonical-skills/backfill', null, { headers: bearerHeader(token) })).data,
  reprocess: async (token: string, opts: { limit?: number; include_manual?: boolean } = {}) =>
    (await apiClient.post('/admin/canonical-skills/reprocess', null, { params: { ...opts }, headers: bearerHeader(token) })).data,
};

// クルー管理API（ゲーミフィケーション・クルー詳細バンドル / ランクサマリ / 育成アドバイス）
// 店長エンドポイント（§5.1 / §6.1 / 付録D）。store スコープのため store_id をパス、management token をクエリで送る。
// store_user スコープ（クルー詳細バンドル）は store_user_id をパスに取り、サーバ側で所有権検証（付録F.1）。
export const crewApi = {
  // クルー管理 ランク別人数 + 育成アドバイス数
  // GET /api/store/{store_id}/crew-ranks/summary
  getCrewRanksSummary: async (storeId: string | null, token: string): Promise<CrewRanksSummary> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    const response = await apiClient.get<CrewRanksSummary>(`/store/${storeId}/crew-ranks/summary`, {
      params: { token },
    });
    return response.data;
  },

  // クルー詳細バンドル {rank, engagement, parameters, skills[], advice[], flags{skills_ready}}（N+1回避の単一読取）
  // GET /api/store-users/{store_user_id}/gamification
  getCrewGamification: async (
    storeUserId: string,
    token: string,
    params: { window_days?: number } = {}
  ): Promise<CrewGamificationBundle> => {
    if (!storeUserId) {
      throw new Error('クルーIDが必要です');
    }
    const response = await apiClient.get<CrewGamificationBundle>(
      `/store-users/${storeUserId}/gamification`,
      { params: { token, ...params } }
    );
    return response.data;
  },

  // クルーの未習得スキル一覧（管理JWT用。tier_code でフィルタ可）
  // GET /api/store-users/{store_user_id}/unacquired-skills
  getUnacquiredSkillsForCrew: async (
    storeUserId: string,
    token: string,
    params: { tier_code?: string } = {}
  ): Promise<UnacquiredSkillsForCrew> => {
    if (!storeUserId) {
      throw new Error('クルーIDが必要です');
    }
    const response = await apiClient.get<UnacquiredSkillsForCrew>(
      `/store-users/${storeUserId}/unacquired-skills`,
      { params: { token, ...params } }
    );
    return response.data;
  },

  // 育成アドバイス一覧取得（store スコープ。status / store_user_id でフィルタ可）
  // GET /api/store/{store_id}/copilot-advice
  getCopilotAdvice: async (
    storeId: string | null,
    token: string,
    params: { status?: AdviceStatus; store_user_id?: string } = {}
  ): Promise<AdviceObject[]> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    const response = await apiClient.get<AdviceObject[]>(`/store/${storeId}/copilot-advice`, {
      params: { token, ...params },
    });
    return response.data;
  },

  // 育成アドバイスの状態更新（dismiss / done など。status は正準値 active|dismissed|done|expired）
  // PATCH /api/store/{store_id}/copilot-advice/{advice_id}
  patchCopilotAdvice: async (
    storeId: string | null,
    adviceId: string,
    status: AdviceStatus,
    token: string
  ): Promise<AdviceObject> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    const response = await apiClient.patch<AdviceObject>(
      `/store/${storeId}/copilot-advice/${adviceId}`,
      { status },
      { params: { token } }
    );
    return response.data;
  },

  // 店舗手入力アドバイス一覧（1クルー複数件、新しい順。未登録なら空配列）
  // GET /api/store-users/{store_user_id}/manual-advice
  listManualAdvices: async (
    storeUserId: string,
    token: string
  ): Promise<StoreManualAdvice[]> => {
    if (!storeUserId) {
      throw new Error('クルーIDが必要です');
    }
    const response = await apiClient.get<StoreManualAdvice[]>(
      `/store-users/${storeUserId}/manual-advice`,
      { params: { token } }
    );
    return response.data;
  },

  // 店舗手入力アドバイス新規登録（1クルー複数件可、改行を含む複数行テキスト）
  // POST /api/store-users/{store_user_id}/manual-advice
  createManualAdvice: async (
    storeUserId: string,
    content: string,
    token: string
  ): Promise<StoreManualAdvice> => {
    if (!storeUserId) {
      throw new Error('クルーIDが必要です');
    }
    const response = await apiClient.post<StoreManualAdvice>(
      `/store-users/${storeUserId}/manual-advice`,
      { content },
      { params: { token } }
    );
    return response.data;
  },

  // 店舗手入力アドバイス更新（advice_id 指定、1件）
  // PUT /api/store-users/{store_user_id}/manual-advice/{advice_id}
  updateManualAdvice: async (
    storeUserId: string,
    adviceId: string,
    content: string,
    token: string
  ): Promise<StoreManualAdvice> => {
    if (!storeUserId || !adviceId) {
      throw new Error('クルーIDとアドバイスIDが必要です');
    }
    const response = await apiClient.put<StoreManualAdvice>(
      `/store-users/${storeUserId}/manual-advice/${adviceId}`,
      { content },
      { params: { token } }
    );
    return response.data;
  },

  // 店舗手入力アドバイス削除（advice_id 指定、1件）
  // DELETE /api/store-users/{store_user_id}/manual-advice/{advice_id}
  deleteManualAdvice: async (
    storeUserId: string,
    adviceId: string,
    token: string
  ): Promise<void> => {
    if (!storeUserId || !adviceId) {
      throw new Error('クルーIDとアドバイスIDが必要です');
    }
    await apiClient.delete(
      `/store-users/${storeUserId}/manual-advice/${adviceId}`,
      { params: { token } }
    );
  },

  // 店舗内ランク再導出  POST /api/store-users/{store_user_id}/gamification-rank/recalculate
  recalculateRank: async (storeUserId: string, token: string): Promise<RankRecalculateResult> => {
    if (!storeUserId) throw new Error('クルーIDが必要です');
    const response = await apiClient.post<RankRecalculateResult>(
      `/store-users/${storeUserId}/gamification-rank/recalculate`, null, { params: { token } });
    return response.data;
  },
};

// 特別オファー / 招待状API（ゲーミフィケーション・§5.1 / §6.1 / 付録D）
// 店長エンドポイント（store スコープ）。store_id をパス、management token をクエリで送る（skillSettingsApi/crewApi と同パターン）。
// 作成と同時に gamification_special_offers へ真実として保持され、既存 LINE パイプラインで push される（§10-4）。
export const offersApi = {
  // 特別オファー / 招待状 作成 + LINE送信
  // POST /api/store/{store_id}/special-offers
  createSpecialOffer: async (
    storeId: string | null,
    data: SpecialOfferCreate,
    token: string
  ): Promise<SpecialOffer> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    const response = await apiClient.post<SpecialOffer>(
      `/store/${storeId}/special-offers`,
      data,
      { params: { token } }
    );
    return response.data;
  },

  // 店長が送信した特別オファー / 招待状の一覧取得（諾否結果の確認用）
  // GET /api/store/{store_id}/store-users/{store_user_id}/special-offers
  // ※クルー本人の受信箱は line_access_token を使う別経路（§5.3）で、management token では呼べない。
  getCrewSpecialOffers: async (
    storeId: string | null,
    storeUserId: string,
    token: string
  ): Promise<SpecialOffer[]> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    if (!storeUserId) {
      throw new Error('クルーIDが必要です');
    }
    const response = await apiClient.get<SpecialOffer[]>(
      `/store/${storeId}/store-users/${storeUserId}/special-offers`,
      { params: { token } }
    );
    return response.data;
  },

  // 店舗全体で返信済み（承諾/見送り）の特別オファー / 招待状の一覧取得
  // GET /api/store/{store_id}/special-offers/replied
  // ※クルー一覧画面の通知バッジ集計用（store_user_id単位に件数を集計して使う）。
  getRepliedSpecialOffers: async (
    storeId: string | null,
    token: string
  ): Promise<SpecialOffer[]> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    const response = await apiClient.get<SpecialOffer[]>(
      `/store/${storeId}/special-offers/replied`,
      { params: { token } }
    );
    return response.data;
  },

  // ---- 特別オファー / 招待状 テンプレート（店舗別化）----
  // 共通（全店）と当該店舗のテンプレをマージ取得。offer_type 省略で全種別。
  // GET /api/store/{store_id}/special-offer-templates
  listTemplates: async (
    storeId: string | null,
    token: string,
    offerType?: SpecialOfferType
  ): Promise<SpecialOfferTemplate[]> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    const response = await apiClient.get<SpecialOfferTemplate[]>(
      `/store/${storeId}/special-offer-templates`,
      { params: { token, offer_type: offerType } }
    );
    return response.data;
  },

  // 店舗別テンプレを作成（共通テンプレの作成は不可）。
  // POST /api/store/{store_id}/special-offer-templates
  createTemplate: async (
    storeId: string | null,
    data: SpecialOfferTemplateInput,
    token: string
  ): Promise<SpecialOfferTemplate> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    const response = await apiClient.post<SpecialOfferTemplate>(
      `/store/${storeId}/special-offer-templates`,
      data,
      { params: { token } }
    );
    return response.data;
  },

  // 店舗別テンプレを部分更新（共通テンプレは 403）。
  // PATCH /api/store/{store_id}/special-offer-templates/{template_id}
  updateTemplate: async (
    storeId: string | null,
    templateId: string,
    data: SpecialOfferTemplateInput,
    token: string
  ): Promise<SpecialOfferTemplate> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    const response = await apiClient.patch<SpecialOfferTemplate>(
      `/store/${storeId}/special-offer-templates/${templateId}`,
      data,
      { params: { token } }
    );
    return response.data;
  },

  // 店舗別テンプレをソフトデリート（共通テンプレは 403）。
  // DELETE /api/store/{store_id}/special-offer-templates/{template_id}
  deleteTemplate: async (
    storeId: string | null,
    templateId: string,
    token: string
  ): Promise<SpecialOfferTemplate> => {
    if (!storeId) {
      throw new Error('店舗IDが必要です');
    }
    const response = await apiClient.delete<SpecialOfferTemplate>(
      `/store/${storeId}/special-offer-templates/${templateId}`,
      { params: { token } }
    );
    return response.data;
  },
};

// 今すぐ募集 ランク別配信API（ゲーミフィケーション・§5.1 / §6.1 / 付録D）
// 既存 lineApi.sendMessage の tier ターゲティング版。target_mode + tier_ids を構造化指定（§9-5）。
export const broadcastApi = {
  // 配信対象人数プレビュー（送信前の対象クルー数）
  // GET /api/line/broadcast/by-rank/count
  byRankCount: async (
    params: BroadcastByRankTarget & { store_id: string },
    token: string
  ): Promise<BroadcastByRankCountResponse> => {
    const response = await apiClient.get<BroadcastByRankCountResponse>(
      '/line/broadcast/by-rank/count',
      {
        params: {
          token,
          store_id: params.store_id,
          target_mode: params.target_mode,
          tier_ids: params.tier_ids,
        },
        // FastAPI の `tier_ids: List[str] = Query()` は repeat 形式
        // （?tier_ids=v1&tier_ids=v2）でないと空配列扱いになる。
        // axios 1.x のデフォルトは `tier_ids[]=v1` 形式で、これだと
        // バックエンドで tier_ids が常に空となり対象人数が 0 になるため、
        // indexes:null を明示して repeat 形式に切り替える。
        paramsSerializer: { indexes: null },
      }
    );
    return response.data;
  },

  // ランク別配信の実行（tier_ids + target_mode）
  // POST /api/line/broadcast/by-rank
  byRank: async (
    body: BroadcastByRankRequest,
    token: string
  ): Promise<BroadcastByRankResponse> => {
    const response = await apiClient.post<BroadcastByRankResponse>(
      '/line/broadcast/by-rank',
      body,
      { params: { token, store_id: body.store_id } }
    );
    return response.data;
  },
};

// 管理画面ワーカー登録API（docs/管理画面ワーカー登録_設計書.md）
// 即払い（即給）を利用しないワーカーを店舗スタッフが事前登録する。
// 登録されたワーカーは sokyu_opt_out=True となり、本人が店舗QR → LINE 登録した際に
// 名寄せ（姓名+生年月日+電話）で紐づく。management token をクエリで送る（crewApi と同パターン）。
export interface AdminWorkerCreatePayload {
  last_name: string;
  first_name: string;
  birth_date: string; // YYYY-MM-DD
  phone: string;
  email: string;
  store_id: string;
  memo?: string;
  // 飛び込み出勤（シフト未応募QR出退勤）に自動適用する既定時給（円）。任意。
  default_hourly_wage?: number | null;
}

export interface AdminWorkerCreateResponse {
  user_id: string;
  store_user_id: string;
  registration_source: string;
  sokyu_opt_out: boolean;
  line_linked: boolean;
  default_hourly_wage: number | null;
}

export interface AdminWorkerListItem {
  user_id: string;
  store_user_id: string;
  name: string;
  phone: string;
  line_linked: boolean;
  sokyu_opt_out: boolean;
  default_hourly_wage: number | null;
  registered_at: string | null;
}

// 店舗所属ワーカーの即払い利用状態（クルー管理一覧の切替UI表示用）
// docs/即給利用切替_設計書.md §5
export interface StoreWorkerSokyuStatusItem {
  store_user_id: string;
  user_id: string;
  name: string;
  sokyu_opt_out: boolean;
  wallet_balance: number; // OFF切替確認ダイアログで表示する未引き出し残高（全企業合算・円）
  default_hourly_wage: number | null; // 飛び込み勤務に自動適用される店舗別の既定時給（円）
}

export interface DefaultHourlyWageUpdateResponse {
  store_user_id: string;
  default_hourly_wage: number | null;
}

export interface SokyuOptOutUpdateResponse {
  user_id: string;
  sokyu_opt_out: boolean;
  wallet_balances: { company_id: string; balance: number }[];
}

// 未連携の事前登録クルー削除の結果。user_deleted=false は
// 法的証跡・即給連携・残高が残っており User 行は保持したことを示す。
export interface AdminWorkerDeleteResponse {
  store_user_id: string;
  user_deleted: boolean;
}

// CSV一括登録の取込結果（課題123）
// CSV取込は名寄せせず各行を新規作成するため、結果は created か alert のみ。
export interface WorkerImportRowResult {
  row: number;
  status: 'created' | 'alert';
  name: string;
  created_user_id: string | null;
  created_store_user_id: string | null;
  error: string | null;
}

export interface WorkerImportResponse {
  total: number;
  created_count: number;
  alert_count: number;
  rows: WorkerImportRowResult[];
}

export const adminWorkerApi = {
  // 即給非利用ワーカーの作成
  // POST /api/admin/workers
  create: async (
    payload: AdminWorkerCreatePayload,
    token: string
  ): Promise<AdminWorkerCreateResponse> => {
    const response = await apiClient.post<AdminWorkerCreateResponse>(
      '/admin/workers',
      payload,
      { headers: bearerHeader(token) }
    );
    return response.data;
  },

  // 管理画面登録ワーカーの一覧（LINE連携状況の確認用）
  // GET /api/admin/workers
  list: async (
    storeId: string,
    token: string
  ): Promise<AdminWorkerListItem[]> => {
    const response = await apiClient.get<AdminWorkerListItem[]>(
      '/admin/workers',
      { params: { store_id: storeId }, headers: bearerHeader(token) }
    );
    return response.data;
  },

  // 店舗所属ワーカー全員の即払い利用状態（切替UIの表示用）
  // GET /api/admin/workers/sokyu-status
  sokyuStatus: async (
    storeId: string,
    token: string
  ): Promise<StoreWorkerSokyuStatusItem[]> => {
    const response = await apiClient.get<StoreWorkerSokyuStatusItem[]>(
      '/admin/workers/sokyu-status',
      { params: { store_id: storeId }, headers: bearerHeader(token) }
    );
    return response.data;
  },

  // 既存ワーカーの即払い利用切替（docs/即給利用切替_設計書.md §4.1）
  // PATCH /api/admin/workers/{user_id}/sokyu-opt-out
  updateSokyuOptOut: async (
    userId: string,
    storeId: string,
    sokyuOptOut: boolean,
    token: string
  ): Promise<SokyuOptOutUpdateResponse> => {
    const response = await apiClient.patch<SokyuOptOutUpdateResponse>(
      `/admin/workers/${userId}/sokyu-opt-out`,
      { sokyu_opt_out: sokyuOptOut },
      { params: { store_id: storeId }, headers: bearerHeader(token) }
    );
    return response.data;
  },

  // 本人未連携の事前登録クルーを削除（③で二重登録になった孤児行の掃除）
  // DELETE /api/admin/workers/{store_user_id}
  deleteUnlinked: async (
    storeUserId: string,
    storeId: string,
    token: string
  ): Promise<AdminWorkerDeleteResponse> => {
    const response = await apiClient.delete<AdminWorkerDeleteResponse>(
      `/admin/workers/${storeUserId}`,
      { params: { store_id: storeId }, headers: bearerHeader(token) }
    );
    return response.data;
  },

  // 店舗別ワーカーの既定時給を更新（飛び込み出勤に自動適用）
  // PATCH /api/admin/workers/{user_id}/default-hourly-wage
  updateDefaultHourlyWage: async (
    userId: string,
    storeId: string,
    defaultHourlyWage: number | null,
    token: string
  ): Promise<DefaultHourlyWageUpdateResponse> => {
    const response = await apiClient.patch<DefaultHourlyWageUpdateResponse>(
      `/admin/workers/${userId}/default-hourly-wage`,
      { default_hourly_wage: defaultHourlyWage },
      { params: { store_id: storeId }, headers: bearerHeader(token) }
    );
    return response.data;
  },

  // CSV一括登録用テンプレート（ヘッダーのみ）を取得（課題123）
  // GET /api/admin/workers/import-template
  importTemplate: async (storeId: string, token: string): Promise<Blob> => {
    const response = await apiClient.get('/admin/workers/import-template', {
      params: { store_id: storeId },
      headers: bearerHeader(token),
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  // CSVでワーカーを一括登録（課題123）
  // POST /api/admin/workers/import-csv
  importCsv: async (
    storeId: string,
    file: File,
    token: string
  ): Promise<WorkerImportResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<WorkerImportResponse>(
      '/admin/workers/import-csv',
      formData,
      {
        params: { store_id: storeId },
        headers: { 'Content-Type': 'multipart/form-data', ...bearerHeader(token) },
      }
    );
    return response.data;
  },
};

const api = {
  jobPosting: jobPostingApi,
  line: lineApi,
  jobShift: jobShiftApi,
  application: applicationApi,
  store: storeApi,
  jobPosition: jobPositionApi,
  industry: industryApi,
  announcement: announcementApi,
  systemSettings: systemSettingsApi,
  storeNotification: storeNotificationApi,
  smokingPrevention: smokingPreventionApi,
  adminStore: adminStoreApi,
  adminCompany: adminCompanyApi,
  adminIdentityUser: adminIdentityUserApi,
  adminAnnouncements: adminAnnouncementsApi,
  faq: faqApi,
  attendance: attendanceApi,
  wallet: walletApi,
  billing: billingApi,
  managementAuth: managementAuthApi,
  managementCompany: managementCompanyApi,
  storeApplication: storeApplicationApi,
  feedback: feedbackApi,
  dashboard: dashboardApi,
  adminApproval: adminApprovalApi,
  adminSokyu: adminSokyuApi,
  adminGlobalRank: adminGlobalRankApi,
  skillSettings: skillSettingsApi,
  crew: crewApi,
  adminWorker: adminWorkerApi,
  offers: offersApi,
  canonicalAdmin: canonicalAdminApi,
  broadcast: broadcastApi,
};

export default api;
